import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Menu from '../menu/Menu';
import RTButton from '../common/RTButton';
import RTSpinner from '../common/RTSpinner';
import Table from '../common/Table';
import SelectPG from '../bedallocation/SelectPG';
import AddInvoiceItem from './AddInvoiceItem';
import { apiGet, apiPost } from '../../api/apiClient';
import { toastSuccess, toastError } from '../common/toast';
import '../common/rtCommon.css';
import './CreateInvoice.css';

const CreateInvoice = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [showSelectPG, setShowSelectPG] = useState(false);
  const [selectedPG, setSelectedPG] = useState(null);
  const [showAddItem, setShowAddItem] = useState(false);
  const [items, setItems] = useState([]);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    invoice_number: '',
    invoice_type: 'monthly',
    invoice_date: new Date().toISOString().split('T')[0], // Today's date
    month_from_date: '',
    month_to_date: '',
    misc_date: '',
    misc_description: '',
    total_amount: 0
  });

  // Table columns definition for invoice items
  const itemsColumns = [
    {
      Header: 'Item Type',
      accessor: 'invoice_item_type',
      sortable: true
    },
    {
      Header: 'Description',
      accessor: 'description',
      sortable: true,
      Cell: ({ value }) => value || '-'
    },
    {
      Header: 'Amount',
      accessor: 'amount',
      sortable: true,
      Cell: ({ value }) => `₹${parseFloat(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      disableSortBy: true,
      Cell: ({ row }) => {
        const itemData = row.original || row.values || row;
        const rowIndex = row.index !== undefined ? row.index : items.findIndex(item => 
          item.invoice_item_type === itemData.invoice_item_type && 
          item.description === itemData.description && 
          item.amount === itemData.amount
        );
        
        return (
          <button
            type="button"
            className="remove-item-btn"
            onClick={() => handleRemoveItem(rowIndex)}
            title="Remove item"
            style={{
              background: '#d32f2f',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '4px 8px',
              cursor: 'pointer',
              fontSize: '12px'
            }}
          >
            Remove
          </button>
        );
      }
    }
  ];

  useEffect(() => {
    generateInvoiceNumber();
  }, []);

  // Auto-load PG if pgId is passed via navigation state
  useEffect(() => {
    const loadPGFromState = async () => {
      const pgId = location.state?.pgId;
      if (pgId && !selectedPG) {
        try {
          const response = await apiGet(`/pgdetails/${pgId}`);
          if (response?.data) {
            setSelectedPG(response.data);
          }
        } catch (error) {
          console.error('Error loading PG from state:', error);
        }
      }
    };
    loadPGFromState();
  }, [location.state]);

  useEffect(() => {
    if (formData.invoice_type === 'monthly') {
      populateCurrentMonthDates();
    } else {
      setFormData(prevData => ({
        ...prevData,
        month_from_date: '',
        month_to_date: ''
      }));
    }
  }, [formData.invoice_type]);

  const generateInvoiceNumber = async () => {
    try {
      const response = await apiGet('/invoicing/invoices/next-number');
      if (response.status === 200) {
        setFormData(prevData => ({
          ...prevData,
          invoice_number: response.data.next_number
        }));
      }
    } catch (error) {
      console.error('Error generating invoice number:', error);
      // Generate a fallback number
      const timestamp = Date.now();
      setFormData(prevData => ({
        ...prevData,
        invoice_number: `INV-${timestamp}`
      }));
    }
  };

  const populateCurrentMonthDates = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    // Format dates in local timezone to avoid UTC conversion issues
    const formatLocalDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    setFormData(prevData => ({
      ...prevData,
      month_from_date: formatLocalDate(firstDay),
      month_to_date: formatLocalDate(lastDay)
    }));
  };

  const handleSelectPG = (pg) => {
    setSelectedPG(pg);
    setShowSelectPG(false);
    setErrors({ ...errors, selectedPG: undefined });
  };

  const handleEditPG = () => {
    setSelectedPG(null);
    setShowSelectPG(true);
  };

  const handleClearPG = () => {
    setSelectedPG(null);
    setErrors({ ...errors, selectedPG: undefined });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevData => ({
      ...prevData,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prevErrors => ({
        ...prevErrors,
        [name]: undefined
      }));
    }
  };

  const calculateTotal = (currentItems) => {
    return currentItems.reduce((total, item) => total + parseFloat(item.amount || 0), 0);
  };

  const getBedRentDescription = (startDate, endDate) => {
    if (!startDate) return '';
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : start;
    const monthName = start.toLocaleString('en-US', { month: 'long' });
    const year = start.getFullYear();
    let description = `${monthName} ${year}`;

    const daysInMonth = new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();
    const segDays = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
    if (segDays < daysInMonth) {
      const startStr = start.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
      const endStr = end.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
      description += ` (${startStr} to ${endStr})`;
    }
    return description;
  };

  // Update Bed Rent item descriptions when billing dates change
  useEffect(() => {
    if (items.length === 0 || !formData.month_from_date) return;
    const hasBedRent = items.some(item => item.invoice_item_type === 'Bed Rent');
    if (!hasBedRent) return;

    const newDescription = getBedRentDescription(formData.month_from_date, formData.month_to_date);
    const updatedItems = items.map(item =>
      item.invoice_item_type === 'Bed Rent' ? { ...item, description: newDescription } : item
    );
    setItems(updatedItems);
  }, [formData.month_from_date, formData.month_to_date]);

  const handleAddItem = (newItem) => {
    const updatedItems = [...items, newItem];
    setItems(updatedItems);
    const newTotal = calculateTotal(updatedItems);
    setFormData(prevData => ({
      ...prevData,
      total_amount: newTotal
    }));
    setShowAddItem(false);
  };

  const handleRemoveItem = (index) => {
    console.log('Removing item at index:', index, 'Current items:', items.length);
    
    if (index < 0 || index >= items.length) {
      console.error('Invalid index for removal:', index);
      toastError('Error removing item: Invalid index');
      return;
    }
    
    const updatedItems = items.filter((_, i) => i !== index);
    console.log('Updated items after removal:', updatedItems.length);
    setItems(updatedItems);
    const newTotal = calculateTotal(updatedItems);
    setFormData(prevData => ({
      ...prevData,
      total_amount: newTotal
    }));
    
    toastSuccess('Item removed successfully');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!selectedPG) {
      newErrors.selectedPG = 'Please select a paying guest';
    }

    if (!formData.invoice_number.trim()) {
      newErrors.invoice_number = 'Invoice number is required';
    }

    if (!formData.invoice_date) {
      newErrors.invoice_date = 'Invoice date is required';
    }

    if (formData.invoice_type === 'monthly') {
      if (!formData.month_from_date) {
        newErrors.month_from_date = 'Month from date is required for monthly invoices';
      }
      if (!formData.month_to_date) {
        newErrors.month_to_date = 'Month to date is required for monthly invoices';
      }
    } else if (formData.invoice_type === 'one_time') {
      // For one-time invoices, we auto-set the billing period to current date
      // No additional validation needed as descriptions are in invoice items
    }

    if (items.length === 0) {
      newErrors.items = 'At least one invoice item is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toastError('Please fix the errors and try again');
      return;
    }

    setLoading(true);
    try {
      // Calculate due date (7 days after invoice date)
      const invoiceDate = new Date(formData.invoice_date);
      const dueDate = new Date(invoiceDate);
      dueDate.setDate(dueDate.getDate() + 7);

      // Calculate billing period based on invoice type
      let billingPeriodStart, billingPeriodEnd;
      if (formData.invoice_type === 'monthly') {
        billingPeriodStart = formData.month_from_date;
        billingPeriodEnd = formData.month_to_date;
      } else {
        // For one-time invoices, use current date for both start and end
        const currentDate = new Date().toISOString().split('T')[0];
        billingPeriodStart = currentDate;
        billingPeriodEnd = currentDate;
      }

      const invoiceData = {
        pg_id: selectedPG.pg_id,
        building_id: selectedPG.building_id || 1, // Use PG's building_id or default
        room_id: selectedPG.room_id || null,
        bed_id: selectedPG.bed_id || null,
        invoice_date: formData.invoice_date,
        due_date: dueDate.toISOString().split('T')[0],
        billing_period_start: billingPeriodStart,
        billing_period_end: billingPeriodEnd,
        invoice_type: formData.invoice_type,
        notes: null, // No notes for one-time invoices as descriptions are in items
        items: items
      };

      const response = await apiPost('/invoicing/invoices', invoiceData);
      
      if (response.status === 200 || response.status === 201) {
        toastSuccess('Invoice created successfully!');
        navigate('/payments');
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      if (error.response?.data?.detail) {
        toastError(`Error creating invoice: ${error.response.data.detail}`);
      } else {
        toastError('Failed to create invoice. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    const fromPgId = location.state?.pgId;
    if (fromPgId) {
      navigate(`/paying-guest/view/${fromPgId}`, { state: { activeTab: 'invoices' } });
    } else {
      navigate('/payments');
    }
  };

  return (
    <div>
      {showSelectPG ? (
        <SelectPG onSelect={handleSelectPG} allocated={true} />
      ) : (
        <div className="rt-layout">
          <Menu className="rt-menu" activePath={location.pathname} />
          <div className="rt-main-content">
            <div className="rt-title-row">
              <h2 className="rt-title">Create Invoice</h2>
              <div className="rt-actions">
                <RTButton variant="secondary" onClick={handleCancel}>
                  &#8592; Back
                </RTButton>
              </div>
            </div>

            <div className="create-invoice-container">
              <form onSubmit={handleSubmit} className="create-invoice-form">
                
                {/* Paying Guest Selection */}
                <div className="rt-form-group">
                  <label>
                    Paying Guest <span style={{ color: 'red' }}>*</span>
                  </label>
                  <div className="pg-selection-container">
                    {!selectedPG ? (
                      <div 
                        className="pg-selection-empty clickable"
                        onClick={() => setShowSelectPG(true)}
                      >
                        <span style={{ fontWeight: 'bold'}}>Select Paying Guest</span>
                      </div>
                    ) : (
                      <div className="pg-selection-filled">
                        <span className="selected-pg-name">{selectedPG.name}</span>
                        {!location.state?.pgId && (
                          <div className="pg-actions">
                            <button 
                              type="button"
                              className="icon-button edit-button" 
                              onClick={handleEditPG}
                              title="Edit"
                            >
                              ✎
                            </button>
                            <button 
                              type="button"
                              className="icon-button clear-button" 
                              onClick={handleClearPG}
                              title="Clear"
                            >
                              ×
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {errors.selectedPG && (
                    <div className="rt-form-error">{errors.selectedPG}</div>
                  )}
                </div>

                {/* Invoice Details */}
                <div className="invoice-details-section">
                  <h3>Invoice Details</h3>
                  
                  <div className="rt-form-row">
                    <div className="rt-form-group">
                      <label>
                        Invoice Number <span style={{ color: 'red' }}>*</span>
                      </label>
                      <input
                        type="text"
                        name="invoice_number"
                        value={formData.invoice_number}
                        onChange={handleInputChange}
                        className="rt-input"
                        placeholder="Auto-generated"
                      />
                      {errors.invoice_number && (
                        <div className="rt-form-error">{errors.invoice_number}</div>
                      )}
                    </div>

                    <div className="rt-form-group">
                      <label>
                        Invoice Date <span style={{ color: 'red' }}>*</span>
                      </label>
                      <input
                        type="date"
                        name="invoice_date"
                        value={formData.invoice_date}
                        onChange={handleInputChange}
                        className="rt-input"
                      />
                      {errors.invoice_date && (
                        <div className="rt-form-error">{errors.invoice_date}</div>
                      )}
                    </div>
                  </div>

                  <div className="rt-form-group">
                    <label>
                      Invoice Type <span style={{ color: 'red' }}>*</span>
                    </label>
                    <select
                      name="invoice_type"
                      value={formData.invoice_type}
                      onChange={handleInputChange}
                      className="rt-input"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="one_time">One-Time</option>
                    </select>
                  </div>

                  {/* Monthly Invoice Fields */}
                  {formData.invoice_type === 'monthly' && (
                    <div className="rt-form-row">
                      <div className="rt-form-group">
                        <label>
                          Month From Date <span style={{ color: 'red' }}>*</span>
                        </label>
                        <input
                          type="date"
                          name="month_from_date"
                          value={formData.month_from_date}
                          onChange={handleInputChange}
                          className="rt-input"
                        />
                        {errors.month_from_date && (
                          <div className="rt-form-error">{errors.month_from_date}</div>
                        )}
                      </div>

                      <div className="rt-form-group">
                        <label>
                          Month To Date <span style={{ color: 'red' }}>*</span>
                        </label>
                        <input
                          type="date"
                          name="month_to_date"
                          value={formData.month_to_date}
                          onChange={handleInputChange}
                          className="rt-input"
                        />
                        {errors.month_to_date && (
                          <div className="rt-form-error">{errors.month_to_date}</div>
                        )}
                      </div>
                    </div>
                  )}

                </div>

                {/* Invoice Items */}
                <div className="invoice-items-section">
                  <div className="items-header">
                    <h3>Invoice Items</h3>
                    <RTButton 
                      type="button"
                      variant="primary" 
                      onClick={() => setShowAddItem(true)}
                    >
                      + Add Item
                    </RTButton>
                  </div>

                  {errors.items && (
                    <div className="rt-form-error">{errors.items}</div>
                  )}

                  {items.length > 0 && (
                    <div className="items-table-container">
                      <Table
                        data={items}
                        columns={itemsColumns}
                        exportable={false}
                        showSearch={false}
                        showPagination={false}
                        showTotalCount={true}
                        totalCountText="Total Items"
                        noDataText="No items added yet"
                      />
                    </div>
                  )}
                </div>

                {/* Total Amount */}
                <div className="total-section">
                  <div className="create-invoice-total-row">
                    <span className="total-label">Total Amount:</span>
                    <span className="total-amount">₹{parseFloat(formData.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="form-actions">
                  <RTButton 
                    type="button" 
                    variant="secondary" 
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    Cancel
                  </RTButton>
                  <RTButton 
                    type="submit" 
                    variant="primary"
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Create Invoice'}
                  </RTButton>
                </div>

              </form>
            </div>

            {/* Add Item Modal */}
            {showAddItem && (
              <AddInvoiceItem
                isCreatingInvoice={true}
                invoice={{
                  billing_period_start: formData.month_from_date,
                  billing_period_end: formData.month_to_date
                }}
                onItemAdded={handleAddItem}
                onClose={() => setShowAddItem(false)}
              />
            )}

            {/* Loading Spinner */}
            {loading && <RTSpinner fullScreen={true} />}

          </div>
        </div>
      )}
    </div>
  );
};

export default CreateInvoice;
